<template>
    <Popup :modelValue=!!PopUpOpen
        @update:model-value="closeChild"
        title="File Download"
        hasClose
    >
        <template #contents v-if=PopUpOpen>
            <div class="grid-item">
            <Spinner v-if="working || deletingInProgress" />
             <div v-else class="content-container">
                    <div class=link-btn>
                        <a @click="getDownloadQueue">⟳ Refresh</a>
                    </div>
                    <Table 
                        :Rows="DowloadQueueRows"
                        :Cols="cols"
                        :Labels="labels"
                        :RowsPerPage=20
                        SplitColKeys>
                        <template #DatetimeRequested="{value}">
                            {{ value.DatetimeRequested.replaceAll("T", " ") }}
                        </template>
                        <template #DatetimeCompleted="{value}">
                            {{ value.DatetimeCompleted?.replaceAll("T", " ") }}
                        </template>
                        <template #FileStatus="{value}">
                            {{ toTitleCase(value.FileStatus) }}
                        </template>
                        <template #FileStatus-1="{ value }">
                            <div v-if="value.FileStatus == 'COMPLETED'">
                                <div v-if="value.Downloading == true" class="download-icon-black" disabled>
                                    <DownloadIconBlack/>
                                </div>
                                <div v-else class="download-icon">
                                    <DownloadIcon @click="downloadFile(value)" />
                                </div>
                            </div>
                            <div v-else>
                            </div>
                        </template>
                        <template #FileStatus-2="{ value }">
                            <div class="delete-icon">
                                <DeleteIcon @click="deleteFileFromTheQueue(value.FileId)" />
                            </div>   
                        </template>
                    </Table>
                    <br>
                </div>
            </div>
        </template>
    </Popup>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import Popup from "./Popup.vue";
import Table from "@/components/TablePersistentChecking.vue";
import { downloadManager, streamDownloadFile } from "@/services/network";
import IFileDownloadList from "@/Interfaces/FileDownloadQueue/IFileDownloadList";
import { DisplayableKeys } from "@/services/SorterService";
import  DownloadIcon from './svg/DownloadIcon.vue'
import  DeleteIcon from './svg/DeleteIcon.vue'
import { authService } from "@/auth/authSingleton";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";
import Spinner from './Spinner.vue';
import DownloadIconBlack from "./svg/DownloadIconBlack.vue";
import { StreamDownloadState } from "@/services/network/StreamDownloadState";

const props = defineProps<{
    modelValue?: boolean;
}>();
const emits = defineEmits<{
    (e: 'update:modelValue', val: boolean): void;
    (e: 'confirm'): void;
}>();
const PopUpOpen = computed<boolean>({
    get() {
        return props.modelValue ?? false; 
    },
    set(val: boolean) {
        emits('update:modelValue', val);
    }
});
const userEmail = computed(() => {
    return authService.getEmailAddress();
});
const working = ref (false)
const deletingInProgress= ref (false)
const DowloadQueueRows = ref <IFileDownloadList[]>([]);
const cols: DisplayableKeys <IFileDownloadList>[]  = [
    'FileType',
    'DatetimeRequested',
    'FileName',
    'FileStatus',
    'DatetimeCompleted',
    'FileStatus',
    'FileStatus'
];
const labels: string[] = [
    "File Type",
    "Date Time Requested",
    "File Name",
    "Status",
    "Date Time Completed",
    "", 
    ""
];
watch(PopUpOpen, async (newValue) => {
    if (newValue) {
        await getDownloadQueue()
    }
});
function closeChild() {
    PopUpOpen.value = false;
    DowloadQueueRows.value = [];
}
async function getDownloadQueue(){
    if (working.value) return;
    working.value = true;
    try{
        const res = (await downloadManager.get({userName: userEmail.value})).data.map(x => ({...x, Downloading : false} ));
        DowloadQueueRows.value = res
    }catch (error) {
        useNotifierWithErrFormatter().error({
            errorType: 'File Download Queue List',
            error
        });
        DowloadQueueRows.value = [];
    } finally {
        working.value = false;
    }    
}
async function deleteFileFromTheQueue(fileId: number){
    if(deletingInProgress.value) return
    try{
        deletingInProgress.value = true
        await downloadManager.delete(undefined, { fileId: fileId });
        await getDownloadQueue()
        useNotifierWithErrFormatter().success({msg: "The file was succesfully deleted"});
    }catch (error){
        useNotifierWithErrFormatter().error({errorType: "File Downlaod", error});
    }finally {
        deletingInProgress.value = false
    }  
}
async function downloadFile(value: IFileDownloadList){
    const result = await streamDownloadFile.streamDownload({fileId:value.FileId.toString()})
    console.log("result: " +  StreamDownloadState[result])
    if (result === StreamDownloadState.FAILURE || result === StreamDownloadState.REDIRECT ) {
        value.Downloading = false;
        if(result === StreamDownloadState.REDIRECT ){
            window.location.reload();
        }
    }
    else{
        value.Downloading = true;
    }
}
function toTitleCase(str) {
  return str.replace(/\w\S*/g, text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
}
</script>

<style scoped>
.grid-item {
    padding: 10px;
    min-height: 70px;
}
.download-icon {
    margin:auto;
    margin-bottom: 2px;
    width: 20px;
    height: 20px;
}
.download-icon-black{
    margin:auto;
    margin-bottom: 2px;
    width: 20px;
    height: 20px;
    stroke: black;
}
.download-icon-black:hover{
    cursor: not-allowed;
}
.download-icon:hover {
    transform: scale(1.2); 
    cursor: pointer;
}
.delete-icon {
    margin:auto;
    width: 20px;
    height: 20px;
}
.delete-icon:hover {
    transform: scale(1.2); 
    cursor: pointer;
}
.link-btn {
    margin-left: auto;
    color:  var(--primary-color)
}
.link-btn:hover {
    cursor: pointer;
}
:deep(.content-container) {
    row-gap: 2px;
}
</style>
